import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const validateField = (name, value) => {
  let error = '';
  if (name === 'name') {
    if (!value.trim()) error = 'Commodity Name is required';
    else if (value.length > 100) error = 'Commodity Name cannot exceed 100 characters';
  }
  if (name === 'hs_code') {
    if (!value.trim()) error = 'HS Code is required';
    else if (value.length > 10) error = 'HS Code cannot exceed 10 characters';
  }
  if (name === 'grade') {
    if (value.trim() && value.length > 50) error = 'Grade cannot exceed 50 characters';
  }
  if (name === 'moisture_pct') {
    if (value !== '' && value !== null) {
      if (isNaN(Number(value))) error = 'Moisture (%) must be a number';
      else if (Number(value) < 0 || Number(value) > 99.99) error = 'Moisture (%) must be between 0 and 99.99';
      else if (!/^\d{0,2}(\.\d{0,2})?$/.test(value)) error = 'Moisture (%) can have up to 2 decimal places';
    }
  }
  if (name === 'origin') {
    if (!value.trim()) error = 'Origin is required';
    else if (value.length > 101) error = 'Origin cannot exceed 101 characters';
  }
  return error;
};

const validateForm = (formData) => {
  const errors = {};
  errors.name = validateField('name', formData.name);
  errors.hs_code = validateField('hs_code', formData.hs_code);
  errors.grade = validateField('grade', formData.grade);
  errors.moisture_pct = validateField('moisture_pct', formData.moisture_pct);
  errors.origin = validateField('origin', formData.origin);
  // Remove empty errors
  Object.keys(errors).forEach((key) => {
    if (!errors[key]) delete errors[key];
  });
  return errors;
};

const CommodityForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    name: '',
    hs_code: '',
    grade: '',
    moisture_pct: '',
    origin: '',
  });
  const [originalFormData, setOriginalFormData] = useState({
    name: '',
    hs_code: '',
    grade: '',
    moisture_pct: '',
    origin: '',
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch commodity data for editing
  useEffect(() => {
    if (isEditMode && id) {
      setInitialLoading(true);
      console.log('Fetching commodity with ID:', id);
      const fetchCommodity = async () => {
        try {
          const url = `${API_BASE_URL}/api/masters/commodity-get/${id}`;
          console.log('Fetching from URL:', url);
          const res = await fetch(url, {
            credentials: 'include',
          });
          console.log('Response status:', res.status);
          console.log('Response headers:', res.headers);
          if (!res.ok) {
            console.error('Response not ok:', res.status, res.statusText);
            throw new Error('Failed to fetch commodity');
          }
          const result = await res.json();
          console.log('Fetched commodity data:', result);
          if (result.data) {
            console.log('Setting form data:', result.data);
            console.log('Individual fields:', {
              name: result.data.name,
              hs_code: result.data.hs_code,
              grade: result.data.grade,
              moisture_pct: result.data.moisture_pct,
              origin: result.data.origin
            });
            const newFormData = {
              name: result.data.name ?? '',
              hs_code: result.data.hs_code ?? '',
              grade: result.data.grade ?? '',
              moisture_pct: result.data.moisture_pct !== null && result.data.moisture_pct !== undefined ? result.data.moisture_pct : '',
              origin: result.data.origin ?? '',
            };
            console.log('Setting new form data:', newFormData);
            setFormData(newFormData);
            setOriginalFormData(newFormData); // Store original values for read-only check
          } else {
            toast.error('Commodity not found');
            navigate('/commodity');
          }
        } catch (error) {
          console.error('Error fetching commodity:', error);
          toast.error('Failed to load commodity data');
          navigate('/commodity');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchCommodity();
    }
  }, [id, isEditMode, navigate]);

  // Debug: Monitor formData changes
  useEffect(() => {
    console.log('FormData changed:', formData);
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Check if field is read-only (has existing value from database)
    const isReadOnly = (fieldName) => {
      // Only check if this is edit mode and the field had an original value
      if (!isEditMode) return false;
      
      // Get the original value from the database (stored separately)
      const originalValue = originalFormData[fieldName];
      return originalValue && originalValue.toString().trim() !== '';
    };
    
    // Don't allow changes to read-only fields
    if (isReadOnly(name)) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]:
        name === 'moisture_pct'
          ? value.replace(/[^0-9.]/g, '')
        : name === 'name' || name === 'origin'
          ? value.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
        : name === 'hs_code'
          ? value.toUpperCase()
          : value
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((msg) => {
        if (msg) toast.error(msg);
      });
      setLoading(false);
      return;
    }
    try {
      const url = isEditMode 
        ? `${API_BASE_URL}/api/masters/commodity-put/${id}`
        : `${API_BASE_URL}/api/masters/commodity-post`;
      
      console.log('=== SUBMITTING COMMODITY DATA ===');
      console.log('URL:', url);
      console.log('Method:', isEditMode ? 'PUT' : 'POST');
      console.log('Form data being sent:', formData);
      
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(isEditMode ? 'Commodity Updated Successfully!' : 'Commodity Registered Successfully!');
        if (!isEditMode) {
          setFormData({
            name: '',
            hs_code: '',
            grade: '',
            moisture_pct: '',
            origin: '',
          });
        } else {
          navigate('/commodity');
        }
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <ToastContainer />
      {initialLoading ? (
        <div className="text-center py-8 text-blue-600">Loading...</div>
      ) : (
        <>
          <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-3 py-1.5">
            <h2 className="text-[15px] font-bold mb-0 text-left">{isEditMode ? 'Edit Commodity' : 'Commodity Entry'}</h2>
            <div className="flex items-center gap-4">
              {isEditMode && (
                <div className="text-[12px] text-gray-600">
                  <span className="inline-block w-3 h-3 bg-gray-100 border border-gray-300 mr-1"></span>
                  Read-only fields
                </div>
              )}
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-2 py-0.5 rounded text-[12px] font-medium hover:bg-[#f0fdf4]"
              >
                <FaArrowLeft />
                Back
              </button>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-0">
            <div className="px-3 pt-4 pb-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-medium mb-1">Commodity Name</label>
                  <input 
                    type="text" 
                    name="name" 
                    value={formData.name} 
                    onChange={handleChange} 
                    onBlur={handleBlur} 
                    className={`w-full border px-3 py-1.5 text-[13px] focus:outline-none rounded-none ${originalFormData.name ? 'border-gray-200 bg-gray-100' : 'border-gray-300 bg-white focus:border-[#15803d]'}`}
                    required 
                    autoComplete="off" 
                    maxLength={100}
                    readOnly={!!originalFormData.name}
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium mb-1">HS Code</label>
                  <input 
                    type="text" 
                    name="hs_code" 
                    value={formData.hs_code} 
                    onChange={handleChange} 
                    onBlur={handleBlur} 
                    className={`w-full border px-3 py-1.5 text-[13px] focus:outline-none rounded-none ${originalFormData.hs_code ? 'border-gray-200 bg-gray-100' : 'border-gray-300 bg-white focus:border-[#15803d]'}`}
                    required 
                    autoComplete="off" 
                    maxLength={10}
                    readOnly={!!originalFormData.hs_code}
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium mb-1">Grade</label>
                  <input 
                    type="text" 
                    name="grade" 
                    value={formData.grade} 
                    onChange={handleChange} 
                    onBlur={handleBlur} 
                    className={`w-full border px-3 py-1.5 text-[13px] focus:outline-none rounded-none ${originalFormData.grade ? 'border-gray-200 bg-gray-100' : 'border-gray-300 bg-white focus:border-[#15803d]'}`}
                    autoComplete="off" 
                    maxLength={50}
                    readOnly={!!originalFormData.grade}
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium mb-1">Moisture (%)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    max="99.99" 
                    name="moisture_pct" 
                    value={formData.moisture_pct || ''} 
                    onChange={handleChange} 
                    onBlur={handleBlur} 
                    className={`w-full border px-3 py-1.5 text-[13px] focus:outline-none rounded-none ${originalFormData.moisture_pct ? 'border-gray-200 bg-gray-100' : 'border-gray-300 bg-white focus:border-[#15803d]'}`}
                    autoComplete="off"
                    readOnly={!!originalFormData.moisture_pct}
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium mb-1">Origin</label>
                  <input 
                    type="text" 
                    name="origin" 
                    value={formData.origin} 
                    onChange={handleChange} 
                    onBlur={handleBlur} 
                    className={`w-full border px-3 py-1.5 text-[13px] focus:outline-none rounded-none ${originalFormData.origin ? 'border-gray-200 bg-gray-100' : 'border-gray-300 bg-white focus:border-[#15803d]'}`}
                    required 
                    autoComplete="off" 
                    maxLength={101}
                    readOnly={!!originalFormData.origin}
                  />
                </div>
              </div>
            </div>
            <div className="px-3 pt-2 pb-4">
              <button type="submit" className="w-auto px-3 py-1.5 text-[13px] bg-[#15803d] text-white font-semibold tracking-wide rounded border border-[#15803d] hover:bg-[#166534] transition disabled:opacity-60" style={{ minWidth: '100px' }} disabled={loading}>
                {loading ? 'Saving...' : (isEditMode ? 'Update Commodity' : 'Save Commodity')}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default CommodityForm;
